import {
  BufferAttribute,
  BufferGeometry,
  CatmullRomCurve3,
  type Plane,
  type Ray,
  Shape,
  ShapeGeometry,
  Vector3,
} from "three";
import type { MutableVec2, Vec2 } from "../airport/AirportDefinition";
import { PATH_DASH } from "../config/gameplay";

/**
 * Where a ray meets a plane, including behind its own origin.
 *
 * `Ray.intersectPlane` rejects a negative parameter, which is the wrong answer
 * for an orthographic camera: on a tall viewport the near plane dips below the
 * ground, so the rays cast from the bottom of the screen start underneath it
 * even though the ground point they name is perfectly well defined. Returns
 * null only when the ray runs parallel to the plane.
 */
export function planePoint(
  ray: Ray,
  plane: Plane,
  out: Vector3,
): Vector3 | null {
  const denominator = plane.normal.dot(ray.direction);
  if (Math.abs(denominator) <= 1e-6) {
    return null;
  }
  const along = -(plane.normal.dot(ray.origin) + plane.constant) / denominator;
  return out.copy(ray.origin).addScaledVector(ray.direction, along);
}

/**
 * Split a polyline into dashes.
 *
 * `offset` walks the pattern along the line, which is what animates the
 * marching dashes on an active flight path.
 */
export function splitDashes(
  points: readonly Vec2[],
  // Annotated, not inferred: `PATH_DASH` is frozen, so an inferred default
  // would narrow these to the literals 3.6 and 2.4 and reject every other
  // pattern — including `PATH_DASH.departureLength`.
  dashLength: number = PATH_DASH.length,
  gapLength: number = PATH_DASH.gap,
  offset = 0,
): MutableVec2[][] {
  const dashes: MutableVec2[][] = [];
  let current: MutableVec2[] = [];

  const period = dashLength + gapLength;
  const phase = ((offset % period) + period) % period;
  let inDash = phase < dashLength;
  let remaining = inDash ? dashLength - phase : period - phase;

  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i]!;
    const to = points[i + 1]!;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const segment = Math.hypot(dx, dy);
    if (segment < 1e-6) {
      continue;
    }

    let travelled = 0;
    while (travelled < segment - 1e-6) {
      const step = Math.min(remaining, segment - travelled);
      const startT = travelled / segment;
      const endT = (travelled + step) / segment;

      if (inDash) {
        const start = { x: from.x + dx * startT, y: from.y + dy * startT };
        const end = { x: from.x + dx * endT, y: from.y + dy * endT };
        if (current.length === 0) {
          current.push(start);
        }
        current.push(end);
      }

      travelled += step;
      remaining -= step;
      if (remaining <= 1e-6) {
        if (inDash && isDrawable(current)) {
          dashes.push(current);
        }
        current = [];
        inDash = !inDash;
        remaining = inDash ? dashLength : gapLength;
      }
    }
  }

  if (isDrawable(current)) {
    dashes.push(current);
  }
  return dashes;
}

/**
 * Rounding the dash phase can leave a strip of zero length; those would
 * become degenerate triangles, so they never make it into the geometry.
 */
function isDrawable(strip: readonly MutableVec2[]): boolean {
  if (strip.length < 2) {
    return false;
  }
  const first = strip[0]!;
  const last = strip[strip.length - 1]!;
  return Math.hypot(last.x - first.x, last.y - first.y) > 1e-6;
}

/** Extrude a polyline into a flat ribbon of constant width. */
export function ribbonGeometry(
  points: readonly Vec2[],
  width: number,
  dashed = false,
  dashOffset = 0,
  dashLength: number = PATH_DASH.length,
  dashGap: number = PATH_DASH.gap,
): BufferGeometry | null {
  if (points.length < 2) {
    return null;
  }

  const strips = dashed
    ? splitDashes(points, dashLength, dashGap, dashOffset)
    : [points];
  const vertexCount = strips.reduce((total, strip) => total + strip.length, 0);
  if (vertexCount < 2) {
    return null;
  }

  const half = width / 2;
  const positions = new Float32Array(vertexCount * 2 * 3);
  const indices: number[] = [];
  let written = 0;

  for (const strip of strips) {
    for (let i = 0; i < strip.length; i++) {
      const before = strip[Math.max(0, i - 1)]!;
      const after = strip[Math.min(strip.length - 1, i + 1)]!;
      let tangentX = after.x - before.x;
      let tangentY = after.y - before.y;
      const length = Math.hypot(tangentX, tangentY) || 1;
      tangentX /= length;
      tangentY /= length;

      const point = strip[i]!;
      const base = (written + i) * 6;
      positions.set(
        [point.x - tangentY * half, point.y + tangentX * half, 0],
        base,
      );
      positions.set(
        [point.x + tangentY * half, point.y - tangentX * half, 0],
        base + 3,
      );
    }

    for (let i = 0; i < strip.length - 1; i++) {
      const base = (written + i) * 2;
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
    written += strip.length;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  return geometry;
}

/**
 * Ribbon with a deterministically ragged edge — used for the river bank so it
 * reads as a natural shoreline rather than a stroked line.
 */
export function raggedRibbonGeometry(
  points: readonly Vec2[],
  width: number,
  jitter: number,
  seed: number,
): BufferGeometry | null {
  if (points.length < 2) {
    return null;
  }

  const dense: MutableVec2[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i]!;
    const to = points[i + 1]!;
    const steps = Math.max(
      1,
      Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / 4),
    );
    for (let step = 0; step < steps; step++) {
      const t = step / steps;
      dense.push({
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      });
    }
  }
  dense.push({ ...points[points.length - 1]! });

  const noise = (index: number, channel: number): number => {
    const value =
      Math.sin(index * 91.17 + channel * 47.31 + seed * 13.73) * 1e4;
    return value - Math.floor(value);
  };

  const positions = new Float32Array(dense.length * 2 * 3);
  const indices: number[] = [];

  for (let i = 0; i < dense.length; i++) {
    const before = dense[Math.max(0, i - 1)]!;
    const after = dense[Math.min(dense.length - 1, i + 1)]!;
    let tangentX = after.x - before.x;
    let tangentY = after.y - before.y;
    const length = Math.hypot(tangentX, tangentY) || 1;
    tangentX /= length;
    tangentY /= length;

    const leftWidth = width / 2 + (noise(i, 1) - 0.5) * jitter * 2;
    const rightWidth = width / 2 + (noise(i, 2) - 0.5) * jitter * 2;
    const point = dense[i]!;

    positions.set(
      [point.x - tangentY * leftWidth, point.y + tangentX * leftWidth, 0],
      i * 6,
    );
    positions.set(
      [point.x + tangentY * rightWidth, point.y - tangentX * rightWidth, 0],
      i * 6 + 3,
    );

    if (i < dense.length - 1) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  return geometry;
}

/** Filled polygon from a closed point loop. */
export function polygonGeometry(points: readonly Vec2[]): ShapeGeometry | null {
  if (points.length < 3) {
    return null;
  }
  const shape = new Shape();
  shape.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i++) {
    shape.lineTo(points[i]!.x, points[i]!.y);
  }
  shape.closePath();
  return new ShapeGeometry(shape);
}

/**
 * Regular polygon centred on the origin — the colour-blind symbols.
 *
 * `rotation` is applied to the first corner, so a 3-gon at PI/2 points up and
 * a 4-gon at PI/4 is a flat-topped square rather than a diamond.
 */
export function regularPolygonGeometry(
  sides: number,
  rotation = 0,
  radius = 1,
): ShapeGeometry | null {
  const corners = Math.floor(sides);
  if (corners < 3) {
    return null;
  }
  const points: MutableVec2[] = [];
  for (let i = 0; i < corners; i++) {
    const angle = rotation + (i / corners) * Math.PI * 2;
    points.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
  }
  return polygonGeometry(points);
}

/** Smooth a polyline through a centripetal Catmull-Rom curve. */
export function resamplePolyline(
  points: readonly Vec2[],
  samples: number,
): MutableVec2[] {
  if (points.length < 3) {
    return points.map((point) => ({ x: point.x, y: point.y }));
  }
  return new CatmullRomCurve3(
    points.map((point) => new Vector3(point.x, point.y, 0)),
    false,
    "centripetal",
  )
    .getPoints(samples)
    .map((point) => ({ x: point.x, y: point.y }));
}

/** Extend both ends of a polyline so it runs past the visible world. */
export function extendPolyline(
  points: readonly Vec2[],
  amount: number,
): MutableVec2[] {
  if (points.length < 2) {
    return points.map((point) => ({ x: point.x, y: point.y }));
  }
  const first = points[0]!;
  const second = points[1]!;
  const last = points[points.length - 1]!;
  const secondLast = points[points.length - 2]!;
  const headLength = Math.hypot(first.x - second.x, first.y - second.y) || 1;
  const tailLength =
    Math.hypot(last.x - secondLast.x, last.y - secondLast.y) || 1;

  return [
    {
      x: first.x + ((first.x - second.x) / headLength) * amount,
      y: first.y + ((first.y - second.y) / headLength) * amount,
    },
    ...points.map((point) => ({ x: point.x, y: point.y })),
    {
      x: last.x + ((last.x - secondLast.x) / tailLength) * amount,
      y: last.y + ((last.y - secondLast.y) / tailLength) * amount,
    },
  ];
}

/** Top-down aeroplane silhouette, used for aircraft ground shadows. */
export function planeSilhouetteGeometry(): ShapeGeometry {
  const shape = new Shape();
  shape.moveTo(1, 0);
  shape.lineTo(0.38, 0.17);
  shape.lineTo(0.02, 0.19);
  shape.lineTo(-0.16, 0.72);
  shape.lineTo(-0.4, 0.72);
  shape.lineTo(-0.29, 0.17);
  shape.lineTo(-0.68, 0.1);
  shape.lineTo(-0.82, 0.3);
  shape.lineTo(-1, 0.3);
  shape.lineTo(-0.91, 0);
  shape.lineTo(-1, -0.3);
  shape.lineTo(-0.82, -0.3);
  shape.lineTo(-0.68, -0.1);
  shape.lineTo(-0.29, -0.17);
  shape.lineTo(-0.4, -0.72);
  shape.lineTo(-0.16, -0.72);
  shape.lineTo(0.02, -0.19);
  shape.lineTo(0.38, -0.17);
  shape.closePath();
  return new ShapeGeometry(shape);
}

/** Dispose every geometry and material under an object, then empty it. */
export function disposeChildren(object: {
  traverse: (callback: (child: unknown) => void) => void;
  clear: () => void;
}): void {
  const geometries = new Set<{ dispose: () => void }>();
  const materials = new Set<{ dispose: () => void }>();

  object.traverse((child) => {
    const mesh = child as {
      geometry?: { dispose: () => void };
      material?: { dispose: () => void } | { dispose: () => void }[];
    };
    if (!mesh.geometry) {
      return;
    }
    geometries.add(mesh.geometry);
    const material = mesh.material;
    if (Array.isArray(material)) {
      for (const item of material) {
        materials.add(item);
      }
    } else if (material) {
      materials.add(material);
    }
  });

  object.clear();
  for (const geometry of geometries) {
    geometry.dispose();
  }
  for (const material of materials) {
    material.dispose();
  }
}
